// js/common/snap-scroll.js
import { playPanelAnimations } from "@/js/common/animation.js";

let snapScrollDestroy = null;

export function initSnapScroll(options = {}) {
  if (typeof snapScrollDestroy === "function") {
    snapScrollDestroy();
    snapScrollDestroy = null;
  }

  const snapArea = document.querySelector(".js-snap-area");
  const snapTrack = document.querySelector(".js-snap-track");
  const panels = document.querySelectorAll(".js-snap-panel");

  const progress = document.querySelector(".js-snap-progress");
  const progressCurrent = document.querySelector(".js-progress-current");

  if (!snapArea || !snapTrack || panels.length === 0) return null;

  const { onPanelChange = null } = options;

  const mediaQuery = window.matchMedia("(min-width: 768px)");
  const wheelQuery = window.matchMedia("(hover: hover) and (pointer: fine)");

  let current = 0;
  let locked = false;
  let wheelAmount = 0;
  let isSnapMode = true;
  let isSnapEnabled = true;
  let isDestroyed = false;

  let touchStartY = 0;
  let touchMoveY = 0;

  const desktopLockTime = 1200;
  const mobileLockTime = 400;
  let lastMoveTime = 0;

  let spSnapHeight = null;
  let lastViewportWidth = window.innerWidth;
  let lastViewportOrientation = getViewportOrientation();

  const cleanupHandlers = [];

  addEvent(window, "snapScroll:disable", disableSnapScroll);
  addEvent(window, "snapScroll:enable", enableSnapScroll);

  addEvent(window, "resize", handleResize);
  addEvent(window, "orientationchange", handleOrientationChange);

  addEvent(window, "pageshow", refreshSnapPosition);
  addEvent(window, "focus", refreshSnapPosition);

  addEvent(document, "visibilitychange", () => {
    if (document.hidden) return;
    if (!isSnapEnabled) return;
    if (isModalOpen()) return;

    refreshSnapPosition();
  });

  addEvent(
    window,
    "wheel",
    (e) => {
      if (!isSnapEnabled) return;
      if (isModalOpen()) return;

      const now = Date.now();

      if (now - lastMoveTime < desktopLockTime) {
        e.preventDefault();
        wheelAmount = 0;
        return;
      }

      const isScrollingUp = e.deltaY < 0;

      if (!isSnapMode) {
        const snapBottom = getSnapBottom();

        if (isScrollingUp && window.scrollY <= snapBottom + 30) {
          e.preventDefault();
          enterSnapFromBottom();
        }

        return;
      }

      e.preventDefault();

      if (locked) {
        wheelAmount = 0;
        return;
      }

      wheelAmount += e.deltaY;

      if (Math.abs(wheelAmount) < 100) return;

      handleSnap(wheelAmount > 0 ? 1 : -1);
      wheelAmount = 0;
    },
    { passive: false }
  );

  addEvent(
    window,
    "touchstart",
    (e) => {
      if (!isSnapEnabled) return;
      if (isModalOpen()) return;
      if (isWheelMode()) return;
      if (e.touches.length === 0) return;

      touchStartY = e.touches[0].clientY;
      touchMoveY = touchStartY;
    },
    { passive: true }
  );

  addEvent(
    window,
    "touchmove",
    (e) => {
      if (!isSnapEnabled) return;
      if (isModalOpen()) return;
      if (isWheelMode()) return;
      if (e.touches.length === 0) return;

      touchMoveY = e.touches[0].clientY;

      const diff = touchStartY - touchMoveY;
      const isSwipeDown = diff < 0;
      const snapBottom = getSnapBottom();

      if (isSnapMode) {
        e.preventDefault();
        return;
      }

      if (isSwipeDown && window.scrollY <= snapBottom + 30) {
        e.preventDefault();
      }
    },
    { passive: false }
  );

  addEvent(
    window,
    "touchend",
    () => {
      if (!isSnapEnabled) return;
      if (isModalOpen()) return;
      if (isWheelMode()) return;

      const now = Date.now();

      if (now - lastMoveTime < getLockTime()) return;

      const diff = touchStartY - touchMoveY;

      if (Math.abs(diff) < 50) return;

      if (!isSnapMode) {
        const isSwipeDown = diff < 0;
        const snapBottom = getSnapBottom();

        if (isSwipeDown && window.scrollY <= snapBottom + 30) {
          enterSnapFromBottom();
        }

        return;
      }

      handleSnap(diff > 0 ? 1 : -1);
    },
    { passive: true }
  );

  addMediaQueryEvent(mediaQuery, resetSnap);
  addMediaQueryEvent(wheelQuery, resetSnap);

  document.body.classList.add("is-snap");
  setSnapHeight({ playAnimations: true });
  updateProgress();

  snapScrollDestroy = destroy;

  return {
    destroy,
    disable: disableSnapScroll,
    enable: enableSnapScroll,
    refresh: refreshSnapPosition,
  };

  function addEvent(target, eventName, handler, options) {
    target.addEventListener(eventName, handler, options);
    cleanupHandlers.push(() => {
      target.removeEventListener(eventName, handler, options);
    });
  }

  function addMediaQueryEvent(mql, handler) {
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      cleanupHandlers.push(() => {
        mql.removeEventListener("change", handler);
      });
      return;
    }

    if (typeof mql.addListener === "function") {
      mql.addListener(handler);
      cleanupHandlers.push(() => {
        mql.removeListener(handler);
      });
    }
  }

  function destroy() {
    if (isDestroyed) return;

    isDestroyed = true;

    cleanupHandlers.forEach((cleanup) => cleanup());
    cleanupHandlers.length = 0;

    locked = false;
    wheelAmount = 0;

    if (snapScrollDestroy === destroy) {
      snapScrollDestroy = null;
    }
  }

  function disableSnapScroll() {
    if (isDestroyed) return;

    isSnapEnabled = false;
    isSnapMode = false;
    locked = false;
    wheelAmount = 0;
    lastMoveTime = 0;

    document.body.classList.remove("is-snap");

    current = 0;

    snapTrack.style.transition = "none";
    snapTrack.style.transform = "translate3d(0, 0, 0)";

    updateProgress();
  }

  function enableSnapScroll() {
    if (isDestroyed) return;

    isSnapEnabled = true;
    isSnapMode = true;
    locked = false;
    wheelAmount = 0;
    lastMoveTime = 0;
    current = clampPanelIndex(current);

    document.body.classList.add("is-snap");

    setSnapHeight({ playAnimations: true });
    updateProgress();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!isSnapEnabled) return;
        if (isDestroyed) return;

        snapTrack.style.transition = "transform 0.75s ease";
        refreshScrollTrigger();
      });
    });
  }

  function resetSnap() {
    if (isDestroyed) return;
    if (!isSnapEnabled) return;
    if (isModalOpen()) return;

    current = clampPanelIndex(current);
    locked = false;
    wheelAmount = 0;
    isSnapMode = true;
    lastMoveTime = 0;
    spSnapHeight = null;

    snapTrack.style.transition = "";
    snapTrack.style.transform = "";

    document.body.classList.add("is-snap");

    setSnapHeight({ playAnimations: true });
    updateProgress();
    refreshScrollTrigger();
  }

  function handleResize() {
    if (isDestroyed) return;
    if (!isSnapEnabled) return;
    if (isModalOpen()) return;

    const viewportWidth = window.innerWidth;
    const viewportOrientation = getViewportOrientation();

    const widthChanged = Math.abs(viewportWidth - lastViewportWidth) > 1;
    const orientationChanged = viewportOrientation !== lastViewportOrientation;

    lastViewportWidth = viewportWidth;
    lastViewportOrientation = viewportOrientation;

    if (isWheelMode()) {
      spSnapHeight = null;
      setSnapHeight();
      refreshScrollTrigger();
      return;
    }

    if (widthChanged || orientationChanged) {
      spSnapHeight = null;
      setSnapHeight();
      refreshScrollTrigger();
    }
  }

  function handleOrientationChange() {
    if (isDestroyed) return;
    if (!isSnapEnabled) return;
    if (isModalOpen()) return;

    spSnapHeight = null;
    lastViewportWidth = window.innerWidth;
    lastViewportOrientation = getViewportOrientation();

    setTimeout(() => {
      if (isDestroyed) return;
      if (!isSnapEnabled) return;
      if (isModalOpen()) return;

      setSnapHeight();
      refreshSnapPosition();
    }, 100);
  }

  function handleSnap(direction) {
    if (!isSnapEnabled) return;
    if (locked) return;
    if (isModalOpen()) return;

    locked = true;
    lastMoveTime = Date.now();

    if (direction > 0) {
      if (current < panels.length - 1) {
        current++;
        movePanel({ playAnimations: true });
      } else {
        releaseSnap();
      }
    } else if (current > 0) {
      current--;
      movePanel({ playAnimations: true });
    }

    setTimeout(() => {
      if (isDestroyed) return;
      if (!isSnapEnabled) return;

      wheelAmount = 0;
      locked = false;
    }, getLockTime());
  }

  function movePanel({ playAnimations = false } = {}) {
    const height = snapArea.clientHeight;

    snapTrack.style.transform = `translate3d(0, -${current * height}px, 0)`;

    updateProgress();

    if (playAnimations) {
      playCurrentPanelAnimations();
    }
  }

  function playCurrentPanelAnimations() {
    const currentPanel = panels[current];

    if (!currentPanel) return;

    if (typeof onPanelChange === "function") {
      onPanelChange(currentPanel);
      return;
    }

    playPanelAnimations(currentPanel);
  }

  function releaseSnap() {
    if (!isSnapEnabled) return;
    if (isModalOpen()) return;

    isSnapMode = false;
    document.body.classList.remove("is-snap");

    wheelAmount = 0;
    lastMoveTime = Date.now();

    window.scrollTo({
      top: getSnapBottom(),
      behavior: "smooth",
    });

    setTimeout(() => {
      if (isDestroyed) return;
      if (!isSnapEnabled) return;

      refreshScrollTrigger();
    }, 800);
  }

  function enterSnapFromBottom() {
    if (!isSnapEnabled) return;
    if (isModalOpen()) return;

    isSnapMode = true;
    document.body.classList.add("is-snap");

    current = panels.length - 1;
    wheelAmount = 0;
    lastMoveTime = Date.now();

    window.scrollTo({
      top: getSnapTop(),
      behavior: "auto",
    });

    snapTrack.style.transition = "none";
    movePanel({ playAnimations: true });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (isDestroyed) return;
        if (!isSnapEnabled) return;

        snapTrack.style.transition = "transform 0.75s ease";
      });
    });

    locked = true;

    setTimeout(() => {
      if (isDestroyed) return;
      if (!isSnapEnabled) return;

      wheelAmount = 0;
      locked = false;
      refreshScrollTrigger();
    }, getLockTime());
  }

  function refreshSnapPosition() {
    if (isDestroyed) return;
    if (!isSnapEnabled) return;
    if (isModalOpen()) return;
    if (!isSnapMode) return;

    setTimeout(() => {
      if (isDestroyed) return;
      if (!isSnapEnabled) return;
      if (isModalOpen()) return;

      setSnapHeight();

      window.scrollTo({
        top: getSnapTop(),
        behavior: "auto",
      });

      snapTrack.style.transition = "none";
      movePanel();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (isDestroyed) return;
          if (!isSnapEnabled) return;

          snapTrack.style.transition = "transform 0.75s ease";
          refreshScrollTrigger();
        });
      });
    }, 100);
  }

  function setSnapHeight({ playAnimations = false } = {}) {
    let height;

    if (isWheelMode()) {
      height = window.innerHeight;
    } else {
      if (!spSnapHeight) {
        spSnapHeight = window.innerHeight;
      }

      height = spSnapHeight;
    }

    document.documentElement.style.setProperty("--snap-height", `${height}px`);
    movePanel({ playAnimations });
  }

// snap-scroll.js

function updateProgress() {
  if (!progress || !progressCurrent) return;

  progress.classList.remove("is-progress-start", "is-progress-end");

  // 1枚目はカウント対象外・ゲージ非表示
  if (current === 0) {
    progress.classList.remove("is-show");
    progressCurrent.style.height = "0%";
    return;
  }

  progress.classList.add("is-show");

  // 2枚目を「壱」として扱う
  const progressIndex = current - 1;
  const progressTotal = Math.max(panels.length - 2, 1);

  // 「壱」の時は赤い装飾だけ表示し、赤いゲージ本体は出さない
  if (progressIndex === 0) {
    progress.classList.add("is-progress-start");
    progressCurrent.style.height = "0%";
    return;
  }

  const progressValue = (progressIndex / progressTotal) * 100;
  const height = Math.min(progressValue, 100);

  // 最後、「伍」の時
  if (progressIndex === progressTotal) {
    progress.classList.add("is-progress-end");
  }

  progressCurrent.style.height = `${height}%`;
}
  
  function getLockTime() {
    return isWheelMode() ? desktopLockTime : mobileLockTime;
  }

  function isWheelMode() {
    return wheelQuery.matches;
  }

  function isModalOpen() {
    return (
      document.documentElement.classList.contains("is-modal-open") ||
      document.body.classList.contains("is-modal-open")
    );
  }

  function getSnapTop() {
    return snapArea.offsetTop;
  }

  function getSnapBottom() {
    return snapArea.offsetTop + snapArea.offsetHeight;
  }

  function getViewportOrientation() {
    return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
  }

  function clampPanelIndex(index) {
    return Math.min(Math.max(index, 0), panels.length - 1);
  }

  function refreshScrollTrigger() {
    if (typeof window.ScrollTrigger !== "undefined") {
      window.ScrollTrigger.refresh();
    }
  }
}